import { Box, Link, SpaceBetween } from '@amzn/awsui-components-react';
import {
  RadarNewsFetchMode,
  type RadarAccountChip,
  type RadarDirectionSummary,
  type RadarRecentNews,
  type RadarSuggestedOrg,
} from '../../../client/IvtHubClient';
import { formatArr } from './scale';
import './RadarCard.css';

/**
 * One direction card on the radar section (Figma A/C/D 4833:16312 / 4833:18479 /
 * 4833:19545; induction §5.1.1).
 *
 * The card is read-only in R17. The title is a Link that calls onOpen so a
 * follow-on row (R18) can wire the detail modal without touching this file;
 * R19 wires the FP / adopt flow at the modal. The Figma's hover "修改" pencil
 * is deliberately NOT rendered — XIN-ANSWERS §5 (2026-10-29): "NOT a feature,
 * not agreed with PM/UX". Rendering a placeholder affordance for a flow that
 * does not exist is worse than leaving it off, because clicking it would
 * either dead-end or do something the user did not ask for.
 *
 * Four sections, in Figma order, each prefixed by the ◆ heading mark:
 *
 *   1. Pipeline evidence badge — distinct-account / opp count / ARR triple.
 *      Rendered even for a news-only direction (counts are 0 server-side,
 *      induction §5.1.1), because dropping it would hide the "both evidence"
 *      ordering the server returns the rows in; `evidenceTier` on the shape
 *      disambiguates and a reader scanning the grid sees the zeros as data.
 *   2. Account chips — one per distinct SFDC account, server-ordered by ARR
 *      descending (induction §5.1.1 "每 account with its ARR"). May be empty
 *      for a news-only direction.
 *   3. Recent news — up to 10 server-side (capped by the smithy list length
 *      — see RadarRecentNewsList). Each row carries the fetch-mode badge
 *      (SYSTEM_FETCHED vs MANUALLY_UPLOADED) and the published date; URL and
 *      sourceKind are rendered when present (sourceKind is absent for a
 *      manual upload with no NEWS_SOURCE row — RadarRecentNews.sourceKind is
 *      optional on the generated type for that reason).
 *   4. Suggested orgs — one line of count plus chips. v1 handler returns
 *      an empty array (XIN-ANSWERS §6; CR-317555795): the card falls back
 *      to "0 家客户" rather than hiding the heading, so the section stays
 *      consistent across cards. When the handler starts populating the
 *      array, the chips light up without a UI change. AWS vs non-AWS
 *      breakdown is NOT shown — v1 does not populate `isAwsCustomer` on
 *      the detail shape and the card shape never carries it, so computing
 *      the sublabel here would always print `(AWS 0 · 非 AWS N)`.
 */
export interface RadarCardProps {
  direction: RadarDirectionSummary;
  /**
   * Called when the title is clicked. R18 wires the detail modal through
   * this; R17 passes a no-op from the section so the title still looks
   * clickable without being a dead affordance.
   */
  onOpen: (directionId: string) => void;
}

/** Format `{N} 家不同客户` / `{N} 笔交易` style metric cells. */
function PipelineMetric({ value, suffix }: { value: number; suffix: string }) {
  return (
    <span className="radar-card__pipeline-metric">
      <span className="radar-card__pipeline-dot">•</span>
      <span className="radar-card__pipeline-value">{value}</span>
      <span className="radar-card__pipeline-suffix">{suffix}</span>
    </span>
  );
}

function PipelineArr({ value }: { value: number }) {
  return (
    <span className="radar-card__pipeline-metric">
      <span className="radar-card__pipeline-dot">•</span>
      <span className="radar-card__pipeline-value">{formatArr(value)}</span>
    </span>
  );
}

function AccountChip({ chip }: { chip: RadarAccountChip }) {
  const name = chip.accountName ?? '';
  const arr = chip.totalArrUsd ?? 0;
  return (
    <span className="radar-card__account-chip" title={name}>
      <span className="radar-card__account-chip-name">{name}</span>
      <span className="radar-card__account-chip-sep">·</span>
      <span className="radar-card__account-chip-arr">{formatArr(arr)}</span>
    </span>
  );
}

/**
 * The S/M fetch-mode badge. Deliberately a small inline letter rather than a
 * Cloudscape Badge: the comp sizes it at 14px in a circle, which Badge cannot
 * approximate without visual drift against the rest of the card typography.
 *
 * `mode` is required on the smithy (RadarRecentNews.fetchMode is non-optional),
 * but the generated TS type widens it to `| undefined`. An unknown mode must
 * NOT default to "Manually uploaded" — rendering the wrong provenance is worse
 * than rendering nothing — so we return null in that case.
 */
function FetchModeBadge({ mode }: { mode: RadarNewsFetchMode | undefined }) {
  if (mode !== RadarNewsFetchMode.SYSTEM_FETCHED && mode !== RadarNewsFetchMode.MANUALLY_UPLOADED) {
    return null;
  }
  const kind = mode === RadarNewsFetchMode.SYSTEM_FETCHED ? 's' : 'm';
  const label = mode === RadarNewsFetchMode.SYSTEM_FETCHED ? 'System-fetched' : 'Manually uploaded';
  return (
    <span className={`radar-card__fetch-badge radar-card__fetch-badge--${kind}`} aria-label={label}>
      {kind.toUpperCase()}
    </span>
  );
}

function NewsRow({ news }: { news: RadarRecentNews }) {
  const title = news.title ?? '';
  const url = news.url;
  return (
    <li className="radar-card__news-row">
      <FetchModeBadge mode={news.fetchMode} />
      {url ? (
        <Link href={url} external target="_blank" variant="primary">
          {title}
        </Link>
      ) : (
        <span className="radar-card__news-title">{title}</span>
      )}
      {news.publishedAt && (
        <span className="radar-card__news-date">{news.publishedAt}</span>
      )}
    </li>
  );
}

function SuggestedOrgChip({ org }: { org: RadarSuggestedOrg }) {
  const name = org.name ?? '';
  const note = org.note;
  return (
    <span className="radar-card__org-chip" title={note ? `${name} · ${note}` : name}>
      <span className="radar-card__org-chip-name">{name}</span>
      {/* v1 handler leaves `note` null when news `string_agg(DISTINCT mentioned_as)`
          returns null across the mention set (induction §5.3). The UI hides the
          note segment then — a lone bullet without a note reads as a layout bug. */}
      {note && (
        <>
          <span className="radar-card__org-chip-sep">·</span>
          <span className="radar-card__org-chip-note">{note}</span>
        </>
      )}
    </span>
  );
}

export function RadarCard({ direction, onOpen }: RadarCardProps) {
  const pipe = direction.pipelineEvidence;
  const chips = direction.accountChips ?? [];
  const news = direction.recentNews ?? [];
  const orgs = direction.suggestedOrgs ?? [];
  const directionId = direction.directionId ?? '';
  const name = direction.name ?? '';
  const rationale = direction.rationale ?? '';

  return (
    <div className="radar-card">
      <SpaceBetween size="s">
        <div className="radar-card__title">
          <Link
            variant="primary"
            fontSize="heading-s"
            onFollow={(e) => {
              e.preventDefault();
              if (directionId) onOpen(directionId);
            }}
          >
            {name}
          </Link>
        </div>

        {/* The rationale is capped by the smithy @length(max: 4096); the card
            truncates with a CSS line clamp rather than slicing the string so a
            screen-reader still gets the whole paragraph. */}
        <Box variant="small" color="text-body-secondary" fontSize="body-s">
          <span className="radar-card__rationale">{rationale}</span>
        </Box>

        <div className="radar-card__section">
          <div className="radar-card__section-heading">
            <span className="radar-card__diamond">◆</span> 管线证据
          </div>
          <div className="radar-card__pipeline-row">
            <PipelineMetric value={pipe?.distinctAccounts ?? 0} suffix="家不同客户" />
            <PipelineMetric value={pipe?.oppCount ?? 0} suffix="笔交易" />
            <PipelineArr value={pipe?.totalArrUsd ?? 0} />
          </div>
          {chips.length > 0 && (
            <div className="radar-card__account-chips">
              {chips.map((chip, i) => (
                <AccountChip key={`${chip.accountName ?? ''}-${i}`} chip={chip} />
              ))}
            </div>
          )}
        </div>

        <div className="radar-card__section">
          <div className="radar-card__section-heading">
            <span className="radar-card__diamond">◆</span> 新闻 · 报告出处(近 3 个月内)
          </div>
          <div className="radar-card__news-legend">
            <FetchModeBadge mode={RadarNewsFetchMode.SYSTEM_FETCHED} />
            <span className="radar-card__legend-label">System-fetched</span>
            <FetchModeBadge mode={RadarNewsFetchMode.MANUALLY_UPLOADED} />
            <span className="radar-card__legend-label">Manually uploaded</span>
          </div>
          {news.length === 0 ? (
            <div className="radar-card__news-empty">暂无近期资讯</div>
          ) : (
            <ul className="radar-card__news-list">
              {news.map((item, i) => (
                <NewsRow key={`${item.title ?? ''}-${item.publishedAt ?? ''}-${i}`} news={item} />
              ))}
            </ul>
          )}
        </div>

        <div className="radar-card__section">
          <div className="radar-card__section-heading">
            <span className="radar-card__diamond">◆</span> 建议接触的机构客户
          </div>
          <div className="radar-card__org-count">
            <span className="radar-card__pipeline-dot">•</span>
            <span className="radar-card__pipeline-value">{orgs.length}</span>
            <span className="radar-card__pipeline-suffix">家客户</span>
          </div>
          {orgs.length > 0 && (
            <div className="radar-card__org-chips">
              {orgs.map((org, i) => (
                <SuggestedOrgChip key={`${org.name ?? ''}-${i}`} org={org} />
              ))}
            </div>
          )}
        </div>
      </SpaceBetween>
    </div>
  );
}
